/* eslint-disable */
import React, {useEffect, useState} from "react"
import {
    AnchorButton,
    ButtonGroup,
    Card,
    Intent,
    PopoverInteractionKind,
    PopoverPosition,
    mergeRefs,
} from "@blueprintjs/core"
import {Popover2, Tooltip2} from "@blueprintjs/popover2"
import {useTranslation} from "react-i18next"
import {useDispatch, useSelector} from "react-redux"
import _ from "lodash"
import DisplayOptionsCard from "./DisplayOptionsCard"
import {
    getRefDbDataContext,
    getState,
    isExporting,
    isLoadingDisplayData,
} from "../../redux/refdb.selector"

import MinimalButton from "../../../core/components/buttons/MinimalButton"
import ActionButton from "../../../core/components/buttons/ActionButton"
import MatrixTypeSelect from "./MatrixTypeSelect"
import FilterOptionPanel from "./FilterOptionPanel"
import {
    cancelReferenceRangesDataAction,
    dataClean,
    exportDetectabilitySummaryAction,
    exportDisplayDataAction,
    loadDemographicsDataAction,
    loadReferenceRangesDataAction,
    setDisplayDataContextAction,
    setGenerateTableType,
} from "../../redux/refdb.actions"
import Matrix from "../../models/matrix"
import ExportOptionsCard from "./ExportOptionsCard"
import GenerateTableType from "../../../shared/enums/generateTableType"
import Filter from "../../models/filter"
import SwitchableValue from "../../models/switchableValue"
import {showToast} from "../../../common/toaster/AppToasterWrapper"
import SettingsCard from "./SettingsCard"
import env from "../../../@biocrates-common-repo/core/context/Env"

export default function DisplayDataConfigBar() {
    const {t} = useTranslation()
    const dispatch = useDispatch()

    const contextData = useSelector(getRefDbDataContext)
    const isLoading = useSelector(isLoadingDisplayData)
    const isExportingDate = useSelector(isExporting)
    const [type, setType] = useState<Matrix>()
    const state = useSelector(getState)

    /**
     * Write changes in local context to redux state
     */
    useEffect(() => {
        if (type) {
            dispatch(dataClean())
            // Change could come from loading settings template, don't update context then (it's redundant)
            if (contextData.matrixType !== type) {
                const context = {
                    ...contextData,
                    species: type.species,
                    material: type.material,
                    matrixType: type,
                }
                dispatch(setDisplayDataContextAction(context))
            }
        }
    }, [type])

    const loadReferenceRangesData = () => {
        console.log("Load data...")
        if (isLoading) {
            dispatch(cancelReferenceRangesDataAction(false))
        } else if (
            (contextData.assay &&
                !SwitchableValue.isValid(contextData.assay)) ||
            (contextData.filters &&
                contextData.filters.filter((filter) => !Filter.isValid(filter))
                    .length > 0)
        ) {
            showToast({
                type: "info",
                message:
                    "No values were selected for one or more of the enabled filter options.",
            })
        } else {
            dispatch(setGenerateTableType(GenerateTableType.referenceRanges))
            dispatch(loadReferenceRangesDataAction())
        }
    }

    const loadDemographicsData = () => {
        console.log("Load data...")
        dispatch(setGenerateTableType(GenerateTableType.demographic))
        dispatch(loadDemographicsDataAction())
    }

    const exportDisplayData = () => {
        if (_.isUndefined(contextData.exportFileType)) {
            showToast({
                type: "info",
                message: "File type not selected.",
            })
        } else if (
            state.generateTable === GenerateTableType.detectabilitySummary &&
            !contextData.sumUpMetaboliteClasses
        ) {
            dispatch(exportDetectabilitySummaryAction())
        } else if (state.generateTable === GenerateTableType.demographic) {
            dispatch(exportDisplayDataAction("Demographic"))
        } else {
            dispatch(exportDisplayDataAction(""))
        }
    }

    // tooltop manual
    const [calculateDataTooltip, setCalculateDataTooltip] = useState(false)

    const toggleCalculateDataTooltip = (value) => setCalculateDataTooltip(value)

    return (
        <div className="DisplayDataConfigBar">
            <AnchorButton
                minimal
                href={env("USER_MANUAL_URL")}
                target="_blank"
                rel="noopener noreferrer"
                text="Information"
            />

            <MatrixTypeSelect
                className={
                    isLoading || isExportingDate
                        ? "CardRowElementDisabled"
                        : "DisplayDataConfigBarElement"
                }
                selectedValue={type}
                setSelectedValue={setType}
                disabled={isLoading || isExportingDate}
            />

            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    marginLeft: "0.4em",
                }}
            >
                <Popover2
                    interactionKind={PopoverInteractionKind.CLICK}
                    position={PopoverPosition.BOTTOM_LEFT}
                    modifiers={{arrow: {enabled: true}}}
                    disabled={isLoading || isExportingDate}
                    content={<FilterOptionPanel />}
                    renderTarget={({
                        isOpen: isPopoverOpen,
                        ref: ref1,
                        ...popoverProps
                    }) => (
                        <Tooltip2
                            content="Set filter options"
                            position={PopoverPosition.BOTTOM}
                            disabled={isPopoverOpen}
                            openOnTargetFocus={false}
                            renderTarget={({
                                isOpen: isTooltipOpen,
                                ref: ref2,
                                ...tooltipProps
                            }) => (
                                <MinimalButton
                                    {...popoverProps}
                                    {...tooltipProps}
                                    active={isPopoverOpen}
                                    elementRef={mergeRefs(ref1, ref2)}
                                    text="Filter options"
                                    rightIcon="caret-down"
                                    disabled={isLoading || isExportingDate}
                                />
                            )}
                        />
                    )}
                />
            </div>
            <Popover2
                className="DisplayDataConfigBarElement"
                interactionKind={PopoverInteractionKind.CLICK}
                position={PopoverPosition.BOTTOM_RIGHT}
                modifiers={{arrow: {enabled: true}}}
                shouldReturnFocusOnClose={false}
                disabled={isLoading || isExportingDate}
                content={<DisplayOptionsCard />}
                renderTarget={({
                    isOpen: isPopoverOpen,
                    ref: ref1,
                    ...popoverProps
                }) => (
                    <Tooltip2
                        content={t("Set display options")}
                        position={PopoverPosition.BOTTOM}
                        disabled={isPopoverOpen}
                        openOnTargetFocus={false}
                        renderTarget={({
                            isOpen: isTooltipOpen,
                            ref: ref2,
                            ...tooltipProps
                        }) => (
                            <MinimalButton
                                {...popoverProps}
                                {...tooltipProps}
                                active={isPopoverOpen}
                                elementRef={mergeRefs(ref1, ref2)}
                                text="Display options"
                                icon="eye-open"
                                rightIcon="caret-down"
                                disabled={isLoading || isExportingDate}
                            />
                        )}
                    />
                )}
            />
            <Popover2
                className="DisplayDataConfigBarElement"
                interactionKind={PopoverInteractionKind.CLICK}
                position={PopoverPosition.BOTTOM_LEFT}
                modifiers={{arrow: {enabled: true}}}
                content={<SettingsCard />}
                renderTarget={({
                    isOpen: isPopoverOpen,
                    ref: ref1,
                    ...popoverProps
                }) => (
                    <Tooltip2
                        content={t("Save/load settings template")}
                        position={PopoverPosition.BOTTOM}
                        disabled={isPopoverOpen}
                        openOnTargetFocus={false}
                        renderTarget={({
                            isOpen: isTooltipOpen,
                            ref: ref2,
                            ...tooltipProps
                        }) => (
                            <MinimalButton
                                {...popoverProps}
                                {...tooltipProps}
                                active={isPopoverOpen}
                                elementRef={mergeRefs(ref1, ref2)}
                                text="Settings"
                                rightIcon="caret-down"
                                disabled={isLoading || isExportingDate}
                            />
                        )}
                    />
                )}
            />

            <ButtonGroup className="DisplayDataConfigBarElement">
                <Tooltip2
                    content={
                        isLoading ? t("Stop calculation") : t("Calculate data")
                    }
                    position={PopoverPosition.BOTTOM}
                >
                    <AnchorButton
                        minimal
                        disabled={
                            (_.isEmpty(contextData.material) &&
                                _.isEmpty(contextData.species)) ||
                            isExportingDate
                        }
                        text={
                            isLoading
                                ? t("Stop calculation")
                                : t("Calculate reference ranges")
                        }
                        onClick={loadReferenceRangesData}
                    />
                </Tooltip2>
                <Tooltip2
                    content={t("Demographics")}
                    position={PopoverPosition.BOTTOM}
                    disabled={calculateDataTooltip}
                >
                    <Popover2
                        interactionKind={PopoverInteractionKind.CLICK}
                        position={PopoverPosition.BOTTOM_RIGHT}
                        modifiers={{arrow: {enabled: true}}}
                        onOpening={() => toggleCalculateDataTooltip(true)}
                        onClosing={() => toggleCalculateDataTooltip(false)}
                        disabled={
                            (_.isEmpty(contextData.material) &&
                                _.isEmpty(contextData.species)) ||
                            isExportingDate
                        }
                        content={
                            <Card>
                                <div className="CardRow">
                                    <AnchorButton
                                        minimal
                                        disabled={
                                            (_.isEmpty(contextData.material) &&
                                                _.isEmpty(
                                                    contextData.species
                                                )) ||
                                            isExportingDate
                                        }
                                        text={
                                            isLoading
                                                ? t("Stop calculation")
                                                : t("Show demographics table")
                                        }
                                        onClick={loadDemographicsData}
                                    />
                                </div>
                            </Card>
                        }
                    >
                        <ActionButton
                            rightIcon="caret-down"
                            intent={Intent.NONE}
                            className="bar-button"
                            disabled={
                                (_.isEmpty(contextData.material) &&
                                    _.isEmpty(contextData.species)) ||
                                isExportingDate
                            }
                        />
                        <MinimalButton
                            text="Generate table"
                            rightIcon="caret-down"
                            disabled={
                                (_.isEmpty(contextData.material) &&
                                    _.isEmpty(contextData.species)) ||
                                isExportingDate
                            }
                        />
                    </Popover2>
                </Tooltip2>
            </ButtonGroup>
            <ButtonGroup className="DisplayDataConfigBarElement">
                <Tooltip2
                    content={t("Export data as file")}
                    position={PopoverPosition.BOTTOM}
                >
                    <ActionButton
                        text="Export"
                        icon="download"
                        disabled={
                            !state.generateTable || isLoading || isExportingDate
                        }
                        onClick={exportDisplayData}
                    />
                </Tooltip2>
                <Popover2
                    interactionKind={PopoverInteractionKind.CLICK}
                    position={PopoverPosition.BOTTOM_RIGHT}
                    modifiers={{arrow: {enabled: true}}}
                    disabled={
                        !state.generateTable || isLoading || isExportingDate
                    }
                    content={
                        <ExportOptionsCard
                            exportDisplayData={exportDisplayData}
                        />
                    }
                    renderTarget={({
                        isOpen: isPopoverOpen,
                        ref: ref1,
                        ...popoverProps
                    }) => (
                        <Tooltip2
                            content={t("Set export options")}
                            position={PopoverPosition.BOTTOM}
                            disabled={isPopoverOpen}
                            openOnTargetFocus={false}
                            renderTarget={({
                                isOpen: isTooltipOpen,
                                ref: ref2,
                                ...tooltipProps
                            }) => (
                                <ActionButton
                                    {...popoverProps}
                                    {...tooltipProps}
                                    active={isPopoverOpen}
                                    elementRef={mergeRefs(ref1, ref2)}
                                    rightIcon="caret-down"
                                    disabled={
                                        !state.generateTable ||
                                        isLoading ||
                                        isExportingDate
                                    }
                                />
                            )}
                        />
                    )}
                />
            </ButtonGroup>

            {contextData.totalSampleNumber && (
                <div style={{display: "flex", marginLeft: "auto"}}>
                    <span
                        style={{
                            display: "inline-block",
                            alignSelf: "flex-end",
                        }}
                    >
                        {`Total number of samples: ${contextData.totalSampleNumber}`}
                    </span>
                </div>
            )}
        </div>
    )
}
